import { CustomerBox } from "@/features/product-and-price/info-boxes/types";
import {
  CustomerPriceParentQuery,
  CustomerPriceParentQueryVariables,
} from "@/gql/graphql";
import { SpacingValues } from "@/lib/utils/SpacingValues";
import { gql, useQuery } from "@apollo/client";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { Box, Spacing } from "@stenajs-webui/core";
import { Banner } from "@stenajs-webui/elements";
import { ErrorScreen, LoadingScreen } from "@stenajs-webui/panels";
import * as React from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router";
import { CustomerPriceParentFragment } from "../fragments/CustomerPriceParentFragment";
import { ParentHistoryEntry } from "../types";
import { CustomerPriceParent } from "./CustomerPriceParent";
import { CustomerPriceParentHeader } from "./CustomerPriceParentHeader";

const query = gql`
  ${CustomerPriceParentFragment}
  query CustomerPriceParent($customerId: ID!) {
    productPrice {
      customers {
        byId(id: $customerId) {
          ...CustomerPriceParent
        }
      }
    }
  }
`;

export interface CustomerPriceParentScreenParams {
  customerId: string;
}

export const CustomerPriceParentScreen: React.FC = () => {
  const { customerId } = useParams() as { customerId: string };

  const { data, error, loading } = useQuery<
    CustomerPriceParentQuery,
    CustomerPriceParentQueryVariables
  >(query, {
    fetchPolicy: "cache-and-network",
    /*
      The parent history comes from a different API to the rest of the
      customer and can fail on its own. Without this, one error throws away
      every field that did resolve and the page has nothing to draw.
    */
    errorPolicy: "all",
    variables: { customerId },
  });

  /*
    Only while there is nothing to draw. The writes ask for this query to
    run again, and cache-and-network turns loading back on every time it
    does - so without the data check the whole page would be replaced by
    the loading screen after every change. The refetch happens against
    what is already on screen instead, and the rows change when it lands.
  */
  if (loading && !data) {
    return (
      <Spacing num={SpacingValues.TWENTYFOUR}>
        <LoadingScreen />
      </Spacing>
    );
  }

  if (!customerId) {
    return <ErrorScreen text={error?.message} />;
  }

  const customer = data?.productPrice.customers.byId as CustomerBox | undefined;

  /**
   * The API keys parents on the booking account number; the index comes
   * from resolving that number, which the server does. Both are kept -
   * the number is what the API and its errors talk about, the index is
   * what makes a row recognisable.
   */
  const history: ParentHistoryEntry[] = (
    data?.productPrice.customers.byId?.parentHistory ?? []
  ).map((entry) => ({
    parentCustomerNo: String(entry.parentCustomerNo),
    parentIndex: entry.parentCustomer?.custIndex ?? null,
    validFrom: entry.validFrom,
    validTo: entry.validTo ?? null,
  }));

  return (
    <>
      <Helmet title="Price parent - Customer - FPP" />
      {/*
        The frame is drawn whether or not anything loaded. A failure here
        is a page with an error on it, not a blank one - and the way back
        to the customer stays where it was.
      */}
      <CustomerPriceParentHeader
        customerId={customerId}
        breadCrumbName={customer?.name ?? customerId}
      />
      <PageLayout>
        {error && (
          <Box spacing={1}>
            <Banner
              variant={"error"}
              headerText={"Could not load the parent history"}
              text={error.message}
            />
          </Box>
        )}

        {customer ? (
          <CustomerPriceParent
            customer={customer}
            history={history}
            error={error}
          />
        ) : (
          !error && (
            <Banner
              variant={"error"}
              text={"This booking account could not be loaded."}
            />
          )
        )}
      </PageLayout>
    </>
  );
};
